/**
 * Arranque: montar la aplicación, y nada más.
 *
 * Two things about this file exist for the development server, and both are load-bearing.
 *
 * No component is defined here. The React plugin makes any module that defines one take hot
 * updates in place, and `App` used to live here: so while `pnpm dev` ran, an edit to a
 * translation string re-ran this whole file inside the open tab, and its last line mounted
 * another copy of the app under the one already there — two tills stacked down the page, then
 * three, until somebody refreshed.
 *
 * The router is imported here and handed to `App`, not imported by it. Editing a module that
 * cannot be updated in place (the tutorial, for one) rebuilds `router.tsx`, and a rebuilt router
 * handed to a running `App` is one nothing has loaded: the page went blank. Imported only from
 * this file, which takes no updates in place, a rebuilt router reloads the page instead.
 *
 * One address is not the app at all: `/pantalla-cliente`, the second window a laptop till opens
 * on a monitor facing the customer. It gets its own page instead of `App`, because `App` would
 * ask it for a device, a sign-in and, after a quiet spell, a PIN, on the customer's side of the
 * counter.
 */

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { esContextoSeguro } from '@abastece/shared';
import { borrarCopiaSiSePidio } from './db/dexie.js';
import { pedirQueConserve } from './lib/almacenamiento.js';
import { COPIA_TOMADA } from './lib/copia-de-tienda.js';
import { traducir } from './i18n/index.js';
import { App } from './App.js';
import { PantallaCliente } from './PantallaCliente.js';
import { esPantallaCliente } from './lib/pantalla-cliente.js';
import { router } from './router.js';
import { ContextoInseguro } from './ui/ContextoInseguro.js';
import './styles.css';

const root = document.getElementById('root');
if (!root) throw new Error('No se encontró el elemento raíz');

// Before anything reads the local database: the last page may have asked for it to go.
await borrarCopiaSiSePidio();

// A copy of the real shop (`pnpm copia`) says so in its tab, since the shop may be in the next one.
if (COPIA_TOMADA) document.title = `${traducir('copiaDeTienda.pestana')} · ${document.title}`;

// That the browser keep what is on this device, instead of clearing it when space runs short.
// Not awaited: Firefox asks the person, and the till does not wait for the answer.
void pedirQueConserve();

/**
 * Decided before React renders anything, not inside a component.
 *
 * Without WebCrypto there is no PIN to verify and no service worker to register, so there is no
 * app to show — and putting the check inside `App` would mean an early return ahead of its
 * hooks, which is a rule this happens to get away with only because the answer never changes.
 */
createRoot(root).render(
  <StrictMode>
    {!esContextoSeguro() ? (
      <ContextoInseguro />
    ) : esPantallaCliente() ? (
      <PantallaCliente />
    ) : (
      <App router={router} />
    )}
  </StrictMode>,
);
